import type {
  GameRoom,
  GameType,
  RoomTickEvent,
  RoomWelcomeEvent,
} from '@goodtime/shared';
import { api, eventSourceUrl } from './client';

/**
 * Create a wheel/slot room. Pass {@code isPrivate: true} (Social MVP)
 * for a friends-only private room — the response carries an
 * {@code inviteCode} (e.g. "GTC-4F2A") the host shares so friends can
 * join by code. Public rooms (the default) get no code.
 */
export function createRoom(
  gameType: GameType,
  themeId?: string,
  isPrivate = false,
): Promise<GameRoom> {
  return api<GameRoom>('/rooms', {
    method: 'POST',
    body: JSON.stringify({ gameType, themeId, isPrivate }),
  });
}

export function fetchRoom(id: string): Promise<GameRoom> {
  return api<GameRoom>(`/rooms/${encodeURIComponent(id)}`);
}

/**
 * Social MVP — resolve a private room from a typed invite code so the
 * caller can join it via the normal stream + JOIN_ROOM path. The
 * server normalises the code (case / GTC- prefix), so pass it roughly
 * as typed. Rejects (404 → thrown) when the code is unknown or the
 * room is archived.
 */
export function resolveRoomByCode(code: string): Promise<GameRoom> {
  return api<GameRoom>(`/rooms/by-code/${encodeURIComponent(code)}`);
}

export interface RoomEventHandlers {
  onWelcome?: (event: RoomWelcomeEvent) => void;
  onTick?: (event: RoomTickEvent) => void;
  onError?: (event: Event) => void;
}

/**
 * Open an SSE connection to the room's event stream. Returns the underlying
 * {@link EventSource} so the caller can {@code .close()} it on cleanup.
 *
 * Server emits two named events: {@code welcome} (once on connect) and
 * {@code tick} (every 5s while the heartbeat ticker runs).
 */
export function subscribeToRoom(roomId: string, handlers: RoomEventHandlers): EventSource {
  const eventSource = new EventSource(eventSourceUrl(`/rooms/${encodeURIComponent(roomId)}/events`));
  if (handlers.onWelcome) {
    eventSource.addEventListener('welcome', (event) => {
      try {
        handlers.onWelcome!(JSON.parse((event as MessageEvent).data) as RoomWelcomeEvent);
      } catch (e) {
        console.error('[rooms] failed to parse welcome event', e);
      }
    });
  }
  if (handlers.onTick) {
    eventSource.addEventListener('tick', (event) => {
      try {
        handlers.onTick!(JSON.parse((event as MessageEvent).data) as RoomTickEvent);
      } catch (e) {
        console.error('[rooms] failed to parse tick event', e);
      }
    });
  }
  if (handlers.onError) {
    eventSource.addEventListener('error', (event) => {
      handlers.onError!(event);
    });
  }
  return eventSource;
}
